import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useCanMessage } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import { sendMessage } from '../lib/api';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Send,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;

/**
 * Composer for starting a new 1:1 conversation with `targetUserId`.
 * On first successful send the backend returns the new conversation
 * id and we navigate to the canonical chat URL.
 */
export default function NewConversation() {
  const navigate = useNavigate();
  const { userId: targetUserId } = useParams();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [peer, setPeer] = useState(null);
  const fileInputRef = useRef(null);
  const { data: canMsg, isLoading: canLoading } = useCanMessage(targetUserId);

  // Lightweight peer name lookup (best effort — falls back to id).
  useEffect(() => {
    if (!targetUserId) return;
    let cancel = false;
    (async () => {
      try {
        const r = await httpClient.get(`${API_URL}/api/social/profile/${targetUserId}`);
        if (!cancel) setPeer(r.data || null);
      } catch {
        if (!cancel) setPeer(null);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [targetUserId]);

  const handleSend = async ({ imageBase64 } = {}) => {
    const body = text.trim();
    if (!body && !imageBase64) return;
    setSending(true);
    try {
      const payload = imageBase64
        ? {
            recipient_id: targetUserId,
            type: 'image',
            text: body,
            image_base64: imageBase64,
          }
        : { recipient_id: targetUserId, type: 'text', text: body };
      const res = await sendMessage(payload);
      const convId = res.data?.conversation_id;
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      if (convId) {
        navigate(`/app/messaggi/${convId}`, { replace: true });
      } else {
        navigate('/app/messaggi', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || t('messages.cannotMessage');
      toast.error(msg);
      setSending(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t('messages.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => handleSend({ imageBase64: reader.result });
    reader.readAsDataURL(file);
  };

  const peerName = peer?.display_name || peer?.name || peer?.username || '…';
  const blocked = !canLoading && canMsg && !canMsg.allowed;

  return (
    <div className={`flex flex-col h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="new-conversation-page">
      <div
        className={`flex-shrink-0 z-30 backdrop-blur-md ${
          isDark ? 'bg-[#080D1A]/85 border-b border-white/10' : 'bg-white/90 border-b border-gray-100'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3 h-14 px-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="new-conv-back-btn"
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDark ? 'bg-white/10' : 'bg-gray-100'
            }`}
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('messages.newMessage')}
            </p>
            <h2 className={`font-display text-base truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {peerName}
            </h2>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6">
        {canLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#E8334A]" />
          </div>
        ) : blocked ? (
          <div
            className={`p-4 rounded-2xl flex items-start gap-3 ${
              isDark ? 'bg-red-500/10' : 'bg-red-50'
            }`}
            data-testid="cannot-message-banner"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
              {canMsg?.reason === 'privacy'
                ? t('messages.cannotMessagePrivacy')
                : canMsg?.reason === 'blocked' || canMsg?.reason === 'banned'
                ? t('messages.cannotMessageBlocked')
                : t('messages.cannotMessageMutual')}
            </p>
          </div>
        ) : canMsg?.mode === 'request' ? (
          <div
            className={`p-4 rounded-2xl ${isDark ? 'bg-amber-500/10' : 'bg-amber-50'}`}
            data-testid="request-mode-banner"
          >
            <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
              {t('messages.requestMode')}
            </p>
          </div>
        ) : null}
      </div>

      {!blocked && (
        <div
          className={`flex-shrink-0 border-t px-3 py-2 ${
            isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'
          }`}
          style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex items-end gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              data-testid="new-conv-attach-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
              } ${sending ? 'opacity-50' : ''}`}
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFile}
              className="hidden"
            />
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('messages.placeholder')}
              rows={1}
              data-testid="new-conv-text-input"
              className={`flex-1 resize-none px-4 py-2.5 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/40 ${
                isDark
                  ? 'bg-white/5 text-white placeholder-white/40'
                  : 'bg-gray-100 text-gray-900 placeholder-gray-400'
              }`}
              style={{ maxHeight: 120 }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!text.trim() || sending}
              data-testid="new-conv-send-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                text.trim() && !sending ? 'bg-[#E8334A] text-white' : 'bg-gray-300 text-gray-500'
              }`}
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
